import { JSResourceForInteraction } from '@atlassian/react-async';
import { createEntryPoint } from '@atlassian/react-entrypoint';
import { fg } from '@atlassian/jira-feature-gating';
import type { JiraJqlScopeInput } from './ui/__generated__/FilterPopupContentQuery.graphql';
import parameters from './ui/__generated__/FilterPopupContentEntryPointQuery.graphql';
import hydrateParameters, {
	type FilterPopupContentEntryPointQuery_hydrateQuery$variables,
	type JiraJqlViewContext,
} from './ui/__generated__/FilterPopupContentEntryPointQuery_hydrateQuery.graphql';

type EntryPointParams = {
	cloudId: string;
	jqlContext: string;
	scope?: JiraJqlScopeInput;
	onFeedbackClick?: (isOpen: boolean) => void;
	isModeSwitcherEnabled?: boolean;
	accountId?: string;
	viewContext?: JiraJqlViewContext | null;
};

export const filterPopupContentEntryPoint = createEntryPoint({
	root: JSResourceForInteraction(
		() =>
			import(
				/* webpackChunkName: "async-jira-filter-refinement-filter-popup-content" */ './ui/FilterPopupContentEntryPoint.tsx'
			),
	),
	getPreloadProps: ({
		cloudId,
		jqlContext,
		scope,
		onFeedbackClick,
		isModeSwitcherEnabled = false,
		accountId,
		viewContext,
	}: EntryPointParams) => {
		const getHydrateQuery = () => {
			if (!isModeSwitcherEnabled) {
				return {};
			}

			// Copied them from `src/packages/jql-builder/jql-builder/src/main.tsx`
			const variables: FilterPopupContentEntryPointQuery_hydrateQuery$variables = {
				accountId: accountId ?? '',
				cloudId,
				includeUser: jqlContext.length > 0 && !!accountId,
				includeJira: jqlContext.length > 0,
				// Remove argument and query variable on jira_nin_last_used_jql cleanup
				jql: '',
				input: {
					jql: jqlContext,
				},
				// Remove argument and query variable on jira_nin_last_used_jql cleanup
				isFilter: false,
				// Remove argument and query variable on jira_nin_last_used_jql cleanup
				filterAri: '',
				viewContext,
				scope,
				isPlanReleaseFeatureToggleEnabled: false,
			};

			if (fg('simple-plans-m2-release-feature-toggle')) {
				variables.isPlanReleaseFeatureToggleEnabled = true;
			}

			return {
				hydrateQuery: {
					options: {
						fetchPolicy: 'store-or-network' as const,
					},
					parameters: hydrateParameters,
					variables,
				},
			};
		};

		return {
			queries: {
				filterRefinementBasicFields: {
					options: {
						fetchPolicy: 'store-or-network' as const,
					},
					parameters,
					variables: {
						cloudId,
						jqlContext,
						scope,
						isModeSwitcherEnabled: fg('list_lovability_improving_filters')
							? isModeSwitcherEnabled
							: false,
					},
				},
				...(fg('list_lovability_improving_filters') ? getHydrateQuery() : {}),
			},
			extraProps: {
				scope,
				onFeedbackClick,
				isModeSwitcherEnabled: fg('list_lovability_improving_filters')
					? isModeSwitcherEnabled
					: false,
				accountId: fg('list_lovability_improving_filters') ? accountId : undefined,
				viewContext: fg('list_lovability_improving_filters') ? viewContext : undefined,
			},
		};
	},
});
